import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const LIMITS = [["max_pages", "Pages max"], ["max_depth", "Profondeur"], ["max_seconds", "Durée max (s)"], ["max_bytes", "Taille max / page (octets)"], ["max_redirects", "Redirections max"], ["max_link_checks", "Liens testés max"]];
const CATS = [["seo", "SEO"], ["seo_local", "SEO local"], ["technique", "Technique"], ["conversion", "Conversion"]];

export function AdminSiteAudit() {
  const [criteria, setCriteria] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/admin/site-criteria").then((r) => setCriteria(r.data));
    api.get("/admin/site-audit-settings").then((r) => setSettings(r.data));
  }, []);

  const upd = (idx, patch) => setCriteria((prev) => prev.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const save = async (c) => {
    await api.put(`/admin/site-criteria/${c.check_id}`, { weight: Number(c.weight), active: c.active, fail_status: c.fail_status, params: c.params || {}, recommendation: c.recommendation });
    toast.success("Contrôle mis à jour");
  };
  const saveSettings = async () => {
    const { data } = await api.put("/admin/site-audit-settings", settings);
    setSettings(data);
    toast.success("Paramètres enregistrés");
  };

  return (
    <div className="space-y-6">
      {settings && (
        <div className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="admin-crawler-settings">
          <h3 className="font-heading font-semibold mb-3">Limites du crawler</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {LIMITS.map(([k, l]) => (
              <div key={k}><label className="text-xs text-slate-400">{l}</label>
                <Input type="number" value={settings.crawler_limits?.[k] ?? ""} onChange={(e) => setSettings({ ...settings, crawler_limits: { ...settings.crawler_limits, [k]: e.target.value } })} className="h-9 mt-1" data-testid={`crawler-limit-${k}`} /></div>
            ))}
          </div>
          <h3 className="font-heading font-semibold mt-5 mb-3">Seuils de statut & pondération des catégories</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div><label className="text-xs text-slate-400">Score « Réussi » ≥</label><Input type="number" value={settings.thresholds?.reussi ?? 75} onChange={(e) => setSettings({ ...settings, thresholds: { ...settings.thresholds, reussi: Number(e.target.value) } })} className="h-9 mt-1" data-testid="threshold-reussi" /></div>
            <div><label className="text-xs text-slate-400">Score « À améliorer » ≥</label><Input type="number" value={settings.thresholds?.ameliorer ?? 50} onChange={(e) => setSettings({ ...settings, thresholds: { ...settings.thresholds, ameliorer: Number(e.target.value) } })} className="h-9 mt-1" data-testid="threshold-ameliorer" /></div>
            {CATS.map(([k, l]) => (
              <div key={k}><label className="text-xs text-slate-400">Poids {l}</label><Input type="number" step={0.1} value={settings.category_weights?.[k] ?? 1} onChange={(e) => setSettings({ ...settings, category_weights: { ...settings.category_weights, [k]: Number(e.target.value) } })} className="h-9 mt-1" data-testid={`cat-weight-${k}`} /></div>
            ))}
          </div>
          <Button size="sm" onClick={saveSettings} data-testid="save-site-settings" className="mt-4 bg-blue-600 hover:bg-blue-700 rounded-lg">Enregistrer les paramètres</Button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Contrôle</TableHead><TableHead>Catégorie</TableHead><TableHead className="w-24">Poids</TableHead><TableHead className="w-36">Statut si échec</TableHead><TableHead className="w-56">Seuils</TableHead><TableHead className="w-20">Actif</TableHead><TableHead className="w-20"></TableHead></TableRow></TableHeader>
          <TableBody>
            {criteria.map((c, idx) => (
              <TableRow key={c.check_id} data-testid={`site-criteria-row-${c.check_id}`}>
                <TableCell className="max-w-xs"><div className="text-sm font-medium">{c.label}</div><div className="text-xs text-slate-400">{c.sub}</div></TableCell>
                <TableCell className="text-xs text-slate-500">{c.category}</TableCell>
                <TableCell><Input type="number" min={0} max={10} step={0.5} value={c.weight} onChange={(e) => upd(idx, { weight: e.target.value })} className="h-8 w-20" data-testid={`site-criteria-weight-${c.check_id}`} /></TableCell>
                <TableCell>
                  <select value={c.fail_status} onChange={(e) => upd(idx, { fail_status: e.target.value })} className="h-8 text-xs rounded-md border border-slate-200 bg-white px-2" data-testid={`site-criteria-fail-${c.check_id}`}>
                    <option value="AMELIORER">À améliorer</option><option value="IMPORTANT">Important</option><option value="CRITIQUE">Critique</option>
                  </select>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(c.params || {}).map(([k, v]) => (
                      <label key={k} className="text-[11px] text-slate-500 flex items-center gap-1">{k}<Input type="number" value={v} onChange={(e) => upd(idx, { params: { ...c.params, [k]: Number(e.target.value) } })} className="h-7 w-16 text-xs" data-testid={`site-criteria-param-${c.check_id}-${k}`} /></label>
                    ))}
                    {!Object.keys(c.params || {}).length && <span className="text-xs text-slate-300">—</span>}
                  </div>
                </TableCell>
                <TableCell><Switch checked={c.active !== false} onCheckedChange={(v) => upd(idx, { active: v })} data-testid={`site-criteria-active-${c.check_id}`} /></TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => save(c)} data-testid={`site-criteria-save-${c.check_id}`} className="h-8 rounded-lg">OK</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
